import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Spinner,
  Modal,
  Form,
} from "react-bootstrap";
import { useCookies } from "react-cookie";
import {
  fetchClassSections,
  fetchTimeTableEntries,
  getUsers,
  fetchAcademicyears,
  markAttendance,
} from "ServerAPI/serverAPI";
import { useToast } from "context/toast-context/ToastProvider";
import { Check, X, Users, Calendar, Clock, AlertTriangle } from "react-feather";
import TimetableGrid from "components/timeTable/TimetableGrid";

const Attendance = () => {
  const [cookies] = useCookies();
  const { showToast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [sectionId, setSectionId] = useState("");
  const [timetableRows, setTimetableRows] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [tempSlot, setTempSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showDateModal, setShowDateModal] = useState(false);
  
  const [participants, setParticipants] = useState([]);
  const [attendanceData, setAttendanceData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Fetch initial data
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        const payload = { userId: cookies.userId };
        const [sectionsRes, academicYearRes, usersRes] = await Promise.all([
          fetchClassSections(payload),
          fetchAcademicyears(payload),
          getUsers(cookies.email, cookies.roleId, cookies.organizationId),
        ]);

        if (sectionsRes?.status === "success" && academicYearRes?.status === "success") {
          const currentYear = academicYearRes?.data?.find(a => a.isCurrent === true)?.academicYearId;
          const filteredSections = sectionsRes?.data?.filter(c => c.academicYear === currentYear) || [];
          setSections(filteredSections);
          setAllUsers(usersRes?.data?.users || []);
        } else {
          showToast("Failed to fetch initial data", "error");
        }
      } catch (error) {
        showToast("Error loading data", "error");
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, [cookies.userId]);

  const resolveCoordinatorNames = (data, users) => {
    const userMap = {};
    users.forEach(u => userMap[u.userId] = u.name);

    return data.map(item => ({
      ...item,
      days: item.days?.map(day => ({
        ...day,
        slots: day.slots?.map(slot => ({
          ...slot,
          entries: slot.entries?.map(entry => ({
            ...entry,
            coordinatorName: userMap[entry.coordinatorId] || "Unknown Teacher"
          }))
        }))
      }))
    }));
  };

  const fetchTimetableData = async () => {
    if (!sectionId) {
      setTimetableRows([]);
      return;
    }
    setLoading(true);
    try {
      const response = await fetchTimeTableEntries({ 
        userId: cookies.userId,
        classSectionId: sectionId 
      });
      if (response && response.status === "success") {
        setTimetableRows(resolveCoordinatorNames(response.data || [], allUsers));
      } else {
        setTimetableRows([]);
      }
    } catch (error) {
      showToast("Error fetching timetable", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimetableData();
    setSelectedSlot(null);
    setParticipants([]);
    setAttendanceData({});

    if (sectionId) {
      const section = sections.find(s => s.classSectionId === sectionId);
      if (section && section.courseParticipants) {
        loadParticipants(section.courseParticipants);
      }
    }
  }, [sectionId, allUsers]);

  const loadParticipants = async (participantIds) => {
    const filtered = allUsers.filter(u => participantIds.includes(u.userId));
    setParticipants(filtered);
    
    const initialAttendance = {};
    filtered.forEach(p => {
      initialAttendance[p.userId] = "present";
    });
    setAttendanceData(initialAttendance);
  };

  const handleSelectSlot = (slot) => {
    setTempSlot(slot);
    // Reset attendance to all present when opening modal
    const resetAttendance = {};
    participants.forEach(p => {
      resetAttendance[p.userId] = "present";
    });
    setAttendanceData(resetAttendance);
    setShowDateModal(true);
  };

  const handleToggleAttendance = (userId, status) => {
    setAttendanceData(prev => ({
      ...prev,
      [userId]: status
    }));
  };

  const handleSaveClick = () => {
    if (!tempSlot || !sectionId || !selectedDate) {
      showToast("Missing required data. Please select a section, slot and date.", "error");
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    const currentSec = sections.find(s => s.classSectionId === sectionId);
    const currentAcademicYear = currentSec?.academicYear || null;
    const timetableId = timetableRows[0]?.timeTableId || null;
    const entry = tempSlot.entries?.[0] || null;

    const students = participants.map(p => ({
      studentId: p.userId,
      status: attendanceData[p.userId] || "present",
    }));

    const pCount = students.filter(s => s.status === "present").length;
    const aCount = students.filter(s => s.status === "absent").length;

    const payload = {
      organizationId: cookies.organizationId,
      academicYearId: currentAcademicYear,
      classSectionId: sectionId,
      timeTableId: timetableId,
      entriesId: entry?.entriesId || null,
      coordinatorId: entry?.coordinatorId || null,
      date: selectedDate,
      day: tempSlot.day,
      slotId: tempSlot.slotId,
      slotName: entry?.slotName || tempSlot.slotType,
      startTime: tempSlot.startTime,
      endTime: tempSlot.endTime,
      slotType: tempSlot.slotType,
      markedBy: cookies.userId,
      students: students,
      summary: {
        total: students.length,
        present: pCount,
        absent: aCount,
      },
    };

    setSubmitting(true);
    try {
      const response = await markAttendance(payload);
      if (response?.status === "success") {
        showToast("Attendance saved successfully!", "success");
        setShowConfirmModal(false);
        setShowDateModal(false);
        setTempSlot(null);
      } else {
        showToast(response?.message || "Failed to save attendance", "error");
      }
    } catch (error) {
      showToast("Error saving attendance", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const currentSection = sections.find(s => s.classSectionId === sectionId);

  // Day mismatch check
  const isDayMismatch = tempSlot && selectedDate &&
    new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase() !== tempSlot.day.toLowerCase();

  const presentCount = Object.values(attendanceData).filter(v => v === "present").length;
  const absentCount = Object.values(attendanceData).filter(v => v === "absent").length;

  return (
    <Container fluid className="p-4 bg-light min-vh-100">
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center justify-content-between bg-white p-3 rounded shadow-sm border-start border-primary border-5">
            <div>
              <h2 className="mb-0 fw-bold text-dark">Attendance Management</h2>
              <p className="text-muted mb-0">Select a section and period from the timetable to start marking attendance.</p>
            </div>
            <div className="text-end">
              <Badge bg="primary" className="p-2 fs-6 mb-1">
                <Calendar size={16} className="me-2" /> {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </Badge>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card className="border-0 shadow-sm mb-4 overflow-hidden">
            <Card.Body className="p-0">
              <TimetableGrid
                timeTableData={timetableRows}
                cookies={cookies}
                section={sectionId}
                setSection={setSectionId}
                onRefresh={fetchTimetableData}
                classSections={sections}
                sectionName={currentSection?.title || "Select a Section"}
                attendanceMode={true}
                onSelectSlot={handleSelectSlot}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Attendance Modal */}
      <Modal
        show={showDateModal}
        onHide={() => { setShowDateModal(false); setTempSlot(null); }}
        centered
        size="lg"
        scrollable
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">
            Mark Attendance — <span className="text-primary">{currentSection?.title}</span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-2">
          {/* Date & Slot Info */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold small text-uppercase text-muted">Session Date</Form.Label>
                <Form.Control 
                  type="date" 
                  value={selectedDate} 
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="border-primary"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              {tempSlot && (
                <div className="p-3 bg-light rounded border h-100 d-flex flex-column justify-content-center">
                  <div className="text-muted small fw-bold text-uppercase mb-1">Period Details</div>
                  <div className="fw-bold text-primary">{tempSlot.day.toUpperCase()} &middot; {tempSlot.startTime} - {tempSlot.endTime}</div>
                  {tempSlot.entries?.[0] && (
                    <div className="text-muted small mt-1">
                      <strong>Coordinator:</strong> {tempSlot.entries[0].coordinatorName}
                    </div>
                  )}
                </div>
              )}
            </Col>
          </Row>

          {/* Day mismatch warning */}
          {isDayMismatch && (
            <div className="alert alert-warning py-2 mb-3 small d-flex align-items-center border-0 shadow-sm" style={{ backgroundColor: '#fff3cd' }}>
              <AlertTriangle size={16} className="me-2 text-warning flex-shrink-0" />
              <div>
                <strong>Warning:</strong> Selected date is a <strong>{new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long' })}</strong>, 
                but the slot is scheduled for <strong>{tempSlot.day.toUpperCase()}</strong>.
              </div>
            </div>
          )}

          {/* Summary badges */}
          <div className="d-flex gap-3 mb-3">
            <Badge bg="secondary" className="px-3 py-2">Total: {participants.length}</Badge>
            <Badge bg="success" className="px-3 py-2">Present: {presentCount}</Badge>
            <Badge bg="danger" className="px-3 py-2">Absent: {absentCount}</Badge>
          </div>

          {/* Student List */}
          <div style={{ maxHeight: '50vh', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '8px' }}>
            <Table hover responsive className="mb-0 align-middle">
              <thead className="bg-light text-uppercase small fw-bold" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  <th className="ps-3 py-2 text-muted" style={{ width: '60px' }}>S.No</th>
                  <th className="py-2">Name</th>
                  <th className="py-2">Email</th>
                  <th className="py-2 text-center" style={{ width: '150px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {participants.length > 0 ? (
                  participants.map((student, index) => {
                    const isPresent = attendanceData[student.userId] === "present";
                    return (
                      <tr key={student.userId} style={{ backgroundColor: isPresent ? 'rgba(25,135,84,0.04)' : 'rgba(220,53,69,0.04)' }}>
                        <td className="ps-3 text-muted font-monospace">{index + 1}</td>
                        <td className="fw-semibold">{student.name}</td>
                        <td className="text-muted small">{student.email}</td>
                        <td className="text-center">
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <Form.Check
                              type="switch"
                              id={`attendance-switch-${student.userId}`}
                              checked={isPresent}
                              onChange={() => handleToggleAttendance(student.userId, isPresent ? "absent" : "present")}
                              className="attendance-toggle"
                            />
                            <span 
                              className={`fw-bold small ${isPresent ? 'text-success' : 'text-danger'}`}
                              style={{ minWidth: '50px' }}
                            >
                              {isPresent ? "Present" : "Absent"}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-4 text-muted">
                      <Users size={32} className="mb-2 opacity-25" />
                      <p className="mb-0 small">No students found for this section.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0 d-flex justify-content-between">
          <Button variant="light" onClick={() => { setShowDateModal(false); setTempSlot(null); }} className="rounded-pill px-4 border">
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveClick} disabled={submitting || participants.length === 0} className="rounded-pill px-4 shadow">
            Save Attendance
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered size="sm">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold fs-6">Confirm Attendance</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">Please review before saving.</p>

          <div className="p-3 bg-light rounded border mb-3">
            <div className="d-flex justify-content-between mb-1">
              <span className="text-muted small fw-bold">Date</span>
              <span className="fw-bold small">{selectedDate}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span className="text-muted small fw-bold">Day</span>
              <span className="fw-bold small text-uppercase">{tempSlot?.day}</span>
            </div>
          </div>

          <div className="d-flex justify-content-between gap-2">
            <div className="text-center flex-fill p-2 rounded border">
              <div className="text-muted small fw-bold">Total</div>
              <div className="h5 mb-0 fw-bold">{participants.length}</div>
            </div>
            <div className="text-center flex-fill p-2 rounded border" style={{ backgroundColor: 'rgba(25,135,84,0.08)' }}>
              <div className="text-success small fw-bold">Present</div>
              <div className="h5 mb-0 fw-bold text-success">{presentCount}</div>
            </div>
            <div className="text-center flex-fill p-2 rounded border" style={{ backgroundColor: 'rgba(220,53,69,0.08)' }}>
              <div className="text-danger small fw-bold">Absent</div>
              <div className="h5 mb-0 fw-bold text-danger">{absentCount}</div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 d-flex justify-content-between">
          <Button variant="light" onClick={() => setShowConfirmModal(false)} className="rounded-pill px-3 border" size="sm">
            Go Back
          </Button>
          <Button variant="success" onClick={handleConfirmSave} disabled={submitting} className="rounded-pill px-3 shadow" size="sm">
            {submitting ? <><Spinner animation="border" size="sm" className="me-1" /> Saving...</> : "Confirm & Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .attendance-toggle .form-check-input {
          width: 2.5em;
          height: 1.3em;
          cursor: pointer;
        }
        .attendance-toggle .form-check-input:checked {
          background-color: #198754;
          border-color: #198754;
        }
        .attendance-toggle .form-check-input:not(:checked) {
          background-color: #dc3545;
          border-color: #dc3545;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e");
        }
        .attendance-toggle .form-check-input:focus {
          box-shadow: none;
        }
      `}</style>
    </Container>
  );
};

export default Attendance;
